import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useNavigate, useParams } from 'react-router-dom'

import AddBeneficiaryModal from '@components/modal/addBeneficiaryModal'
import LoadingModal from '@components/loading/loadingModal'
import Header from '@pages/companies/header'
import ArrowBack from '@components/arrowBack'
import BeneficiaryTable from '@components/table/beneficiaryTable'
import ClassificationMetrics from '@components/classificationMetrics'
import Loading from '@components/loading'
import colors from '@settings/styleSettings'
import PrimaryButton from '@components/inputs/button/primaryButton'

import BeneficiaryAdd from './beneficiaryAdd'

import {
  getBeneficiariesWithCollection as getBeneficiariesAction,
  getReportToInsurance as getReportToInsuranceAction,
} from '@actions/beneficiary'

import { getProgramme as getProgrammeAction } from '@actions/programme'
import { listMessages as listMessagesAction } from '@actions/notification'

import {
  Container,
  SearchContainer,
  Title,
  InfoProgramme,
} from './styles'
import { PanelContainer } from '../../companies/styles'

const defaultPageSize = 10

const ProgrammeInfos = ({
  getBeneficiariesWithCollection,
  getProgramme,
  programme,
  authData,
  listMessages,
}) => {
  const navigate = useNavigate()
  const { programmeId, insurance: insuranceParam } = useParams()

  const [loadingOverview, setLoadingOverview] = useState(false)
  const [reportLoading, setReportLoading] = useState(false)
  const [, setLoadingMessages] = useState(false)
  const [addBeneficiaryModal, setAddBeneficiaryModal] = useState(false)
  const [filterState, setFilterState] = useState({
    highRisk: false,
    mediumRisk: false,
    lowRisk: false,
    noRisk: false,

    diabetic: false,
    amputation: false,
    diabeticFoot: false,
    nephropathy: false,
    type2: false,
    controlledGlycatedHemoglobin: false,
    diabeticFootEvaluated: false,
    alcohol: false,
    smoking: false,

    dyslipidemic: false,
    hypertensive: false,
    controlledHypertension: false,

    overweight: false,
    overweightInRecovery: false,
  })

  const planId = programmeId || authData?.programme?.id

  const searchBeneficiaries = (searchTerm, newPage, newPageSize = defaultPageSize, callback) => {
    getBeneficiariesWithCollection(
      {
        page: newPage,
        ...filterState,
        searchTerm,
        pageSize: newPageSize,
        programmeId: planId,
      },
      () => callback && callback()
    )
  }

  const goBack = () => {
    navigate(-1)
  }

  const renderName = () => {
    if (authData && authData.programme) {
      return `Programa ${authData.programme.name}`
    }
    if (programme) {
      return `Programa ${programme.name}`
    }
    return ''
  }

  // Navegação robusta para a Agenda de Atividades
  const goToAgenda = () => {
    let slug = insuranceParam
    if (!slug) {
      // fallback via pathname: assume 1º segmento como :insurance
      const parts = window.location.pathname.split('/').filter(Boolean)
      slug = parts[0] || ''
    }
    if (slug) {
      navigate(`/${slug}/acompanhamento/agenda-de-atividades`)
    } else {
      // fallback relativo (caso já esteja em .../acompanhamento/*)
      navigate('../agenda-de-atividades')
    }
  }

  useEffect(() => {
    if (!planId) return
    setLoadingOverview(true)
    getProgramme(planId, () => {
      setLoadingOverview(false)
    })
    listMessages(
      {
        pageSize: 3,
        page: 0,
        orderByDesc: true,
        orderByAsc: false,
        programmeId: planId,
        companyId: 0,
      },
      () => setLoadingMessages(false)
    )
  }, [getProgramme, listMessages, planId])

  return (
    <Container>
      <Header />

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '10px 24px',
          marginBottom: 12,
        }}
      >
        <PrimaryButton
          style={{ maxWidth: 240 }}
          onClick={goToAgenda}
        >
          Agenda de atividades
        </PrimaryButton>
      </div>

      <SearchContainer>
        <InfoProgramme>
          {authData && !authData.programme && <ArrowBack onClick={goBack} />}
          <Title>{renderName()}</Title>
        </InfoProgramme>

        {loadingOverview ? (
          <Loading
            color={colors.primary}
            height={40}
            width={40}
            style={{ marginLeft: '12px' }}
          />
        ) : (
          <PanelContainer>
            <ClassificationMetrics
              beneficiaryFilter={filterState}
              setBeneficiaryFilter={setFilterState}
              companyId={{ programmeId: planId }}
            />
          </PanelContainer>
        )}

        <BeneficiaryAdd openAddBeneficiaryModal={setAddBeneficiaryModal} />
        {addBeneficiaryModal && (
          <AddBeneficiaryModal
            isOpen={addBeneficiaryModal}
            programme={{ id: planId }}
            setClose={() => setAddBeneficiaryModal(false)}
          />
        )}

        <BeneficiaryTable
          onGetReport={(value) => setReportLoading(value)}
          setBeneficiaryFilterState={setFilterState}
          beneficiaryFilterState={filterState}
          searchBeneficiaries={searchBeneficiaries}
          programmeId={planId}
        />
      </SearchContainer>

      <LoadingModal isOpen={reportLoading} />
    </Container>
  )
}

ProgrammeInfos.propTypes = {
  getBeneficiariesWithCollection: PropTypes.func.isRequired,
  getProgramme: PropTypes.func.isRequired,
  listMessages: PropTypes.func.isRequired,
  programme: PropTypes.shape({
    name: PropTypes.string,
  }),
  authData: PropTypes.shape({
    company: PropTypes.shape({
      endPoint: PropTypes.string,
      id: PropTypes.number,
    }),
    programme: PropTypes.shape({
      name: PropTypes.string,
      endPoint: PropTypes.string,
      id: PropTypes.number,
    }),
  }),
}

ProgrammeInfos.defaultProps = {
  programme: null,
  authData: null,
}

const mapStateToProps = ({ beneficiary, auth, programme }) => ({
  beneficiaries: beneficiary.beneficiaries,
  total: beneficiary.total,
  authData: auth.data,
  programme: programme.programme,
})

const mapDispatchToProps = (dispatch) => bindActionCreators(
  {
    getBeneficiariesWithCollection: getBeneficiariesAction,
    getReportToInsurance: getReportToInsuranceAction,
    getProgramme: getProgrammeAction,
    listMessages: listMessagesAction,
  },
  dispatch
)

export default connect(mapStateToProps, mapDispatchToProps)(ProgrammeInfos)
